import { useMemo, useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { commitmentsApi, manpowerApi, usersApi, type CommitmentRow, type CommitmentLine } from '@/lib/apiClient';
import { StateView } from '@/components';
import { fmtDate } from '@/lib/dates';
import AccrualsPanel from './AccrualsPanel';

// Commitment tab (replaces the Cost tab). Per cost code: budget → committed
// (PO+sub) → spent (GL) → planned buyout → remaining to buy out, with the actual
// commitment lines behind each code and a Commitment Register. Read-only from
// Sage; only the buyout plan (planned amount / vendor / note) and the month-end
// accrual schedule (Accruals view — AccrualsPanel) are Project-Tracker-owned.
// Gated financials.view AND showProjectCosts.

const money = (v: number | null | undefined) => v == null ? '—' : '$' + Math.round(v).toLocaleString('en-US');
const moneyC = (v: number) => {
  const a = Math.abs(v), sign = v < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
};
const parseNum = (s: string) => Number(String(s).replace(/[^0-9.\-]/g, ''));
type View = 'code' | 'type' | 'register' | 'accruals';
interface PlanDraft { amount: string; vendor: string; note: string; }

const css = `
  .cm-prov { display:flex; align-items:center; gap:var(--ew-s-3); flex-wrap:wrap; background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:10px var(--ew-s-4); margin-bottom:var(--ew-s-4); }
  .cm-lock { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:#8A4E12; background:#FBEEE1; border-radius:3px; padding:4px 9px; }
  .cm-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(175px,1fr)); gap:var(--ew-s-3); margin-bottom:var(--ew-s-4); }
  .cm-kpi { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:14px 16px 14px 18px; position:relative; overflow:hidden; }
  .cm-kpi::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--acc,var(--ew-n-300)); }
  .cm-kpi .l { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .cm-kpi .v { font:var(--ew-fw-bold) 24px/1.1 var(--ew-font); font-variant-numeric:tabular-nums; color:var(--ew-dark); margin-top:6px; }
  .cm-kpi .s { font-size:12px; color:var(--ew-n-500); margin-top:4px; }
  .cm-card { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:var(--ew-s-4) var(--ew-s-5); }
  .cm-card h3 { font:var(--ew-fw-semibold) 14px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-dark); margin:0; }
  .cm-seg { display:inline-flex; gap:4px; }
  .cm-seg button { border:1px solid var(--ew-n-200); background:#fff; color:var(--ew-n-600); cursor:pointer; border-radius:var(--ew-r-md); padding:6px 12px; font:var(--ew-fw-medium) 12px/1 var(--ew-font); }
  .cm-seg button.active { background:var(--ew-dark); border-color:var(--ew-dark); color:#fff; }
  .cm-seg button:disabled { opacity:.4; cursor:not-allowed; }
  .cm-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:8px 14px; cursor:pointer; border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-dark); }
  .cm-btn:hover:not(:disabled){ background:var(--ew-n-50); }
  .cm-btn.primary { background:var(--ew-green); border-color:var(--ew-green); color:#fff; }
  .cm-btn:disabled { opacity:.5; cursor:not-allowed; }
  .cm-tbl { width:100%; border-collapse:collapse; font-size:13px; }
  .cm-tbl th { text-align:right; padding:10px 12px; font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.05em; text-transform:uppercase; color:var(--ew-n-500); border-bottom:1px solid var(--ew-n-100); white-space:nowrap; }
  .cm-tbl th:first-child, .cm-tbl td:first-child { text-align:left; }
  .cm-tbl td { padding:9px 12px; text-align:right; font-variant-numeric:tabular-nums; border-bottom:1px solid #F2F2F1; color:var(--ew-dark); vertical-align:top; }
  .cm-tbl tfoot td { font-weight:700; border-top:2px solid var(--ew-n-200); border-bottom:none; }
  .cm-tbl tr.over td { background:#FDF6EF; }
  .cm-caret { cursor:pointer; color:var(--ew-n-500); margin-right:8px; font-size:11px; display:inline-block; width:10px; }
  .cm-tag { display:inline-block; margin-left:8px; padding:1px 6px; border-radius:var(--ew-r-sm); font:var(--ew-fw-semibold) 9.5px/1.6 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; vertical-align:middle; }
  .cm-tag.over { background:#FBEEE1; color:#B5611A; border:1px solid #F0D9C0; }
  .cm-tag.unbud { background:#FBEEE1; color:#8A4E12; border:1px solid #F0D9C0; }
  .cm-tag.open { background:var(--ew-n-50); color:var(--ew-n-600); border:1px solid var(--ew-n-200); }
  .cm-tag.stale { background:#FBEEE1; color:#B5611A; border:1px solid #F0D9C0; }
  .cm-tbl tr.stale td { background:#FDF6EF; }
  .cm-alert { display:flex; align-items:center; gap:8px; background:#FBEEE1; border:1px solid #F0D9C0; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12.5px; color:#8A4E12; }
  .cm-hint { display:flex; align-items:flex-start; gap:8px; background:#F6FAF7; border:1px solid #DCEBE0; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12px; color:#3D5A46; line-height:1.45; }
  .cm-typechip { display:inline-block; padding:1px 7px; border-radius:var(--ew-r-pill); font:var(--ew-fw-semibold) 10px/1.6 var(--ew-font); }
  .cm-typechip.po { background:#e7f0f8; color:#15659E; }
  .cm-typechip.sub { background:#EFEAF7; color:#5B3FA0; }
  .cm-lines { background:#FAFAF9; }
  .cm-lines table { width:100%; border-collapse:collapse; font-size:12px; }
  .cm-lines td { padding:5px 12px; border-bottom:1px solid #EFEFEE; text-align:right; color:var(--ew-n-700); }
  .cm-lines td:first-child, .cm-lines td:nth-child(2), .cm-lines td:nth-child(3) { text-align:left; }
  .cm-input { width:120px; text-align:right; font-variant-numeric:tabular-nums; font-size:13px; padding:5px 7px; border:1px solid #CFE6D5; border-radius:3px; background:#F4F9F5; font-family:inherit; }
  .cm-planedit { display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; padding:8px 12px; background:#F4F9F5; }
  .cm-planedit label { display:block; font:var(--ew-fw-semibold) 10px/1.4 var(--ew-font); letter-spacing:.05em; text-transform:uppercase; color:var(--ew-n-600); margin-bottom:3px; }
  .cm-planedit input { font:inherit; font-size:12.5px; padding:5px 8px; border:1px solid var(--ew-n-300); border-radius:3px; background:#fff; font-family:inherit; }
  .cm-editbanner { background:#F4F9F5; border:1px solid #CFE6D5; border-radius:var(--ew-r-md); padding:9px 12px; margin-bottom:12px; font-size:12.5px; color:#1c5b30; }
  .cm-search { font:inherit; font-size:13px; padding:6px 10px; border:1px solid var(--ew-n-300); border-radius:var(--ew-r-md); background:#fff; width:260px; font-family:inherit; }
  .cm-legend { display:flex; flex-wrap:wrap; gap:6px 16px; font-size:11.5px; color:#5A5A56; margin-top:12px; }
`;

interface CmRow extends CommitmentRow { remaining: number; over: boolean; uncommitted: boolean; }
const enrich = (r: CommitmentRow): CmRow => ({
  ...r,
  remaining: (r.budget) - r.committed - r.planned,
  over: r.committed > r.budget,
  uncommitted: r.budget > 0 && r.committed === 0,
});

export default function CommitmentsTab({ projectId }: { projectId: number }) {
  const { data: config } = useQuery({ queryKey: ['manpower', 'config'], queryFn: manpowerApi.config, staleTime: 60_000 });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  const showCosts = config?.showProjectCosts === '1';
  const q = useQuery({ queryKey: ['commitments', projectId], queryFn: () => commitmentsApi.get(projectId), enabled: showCosts, staleTime: 60_000 });
  const qc = useQueryClient();
  const canEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('financials.edit_forecast') ?? false);

  const [view, setView] = useState<View>('code');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [planDraft, setPlanDraft] = useState<Record<string, PlanDraft>>({});
  const [search, setSearch] = useState('');
  // The Accruals view keeps its own draft; this lets the view switcher lock while
  // it is open, so switching away cannot silently throw typed lines away.
  const [accrualsEditing, setAccrualsEditing] = useState(false);

  const saveMut = useMutation({
    mutationFn: (plans: { costCode: string; plannedAmount: number; vendor?: string | null; note?: string | null }[]) => commitmentsApi.savePlan(projectId, plans),
    onSuccess: (data) => { qc.setQueryData(['commitments', projectId], data); setEditing(false); setPlanDraft({}); },
  });

  const model = useMemo(() => {
    if (!q.data) return null;
    const rows = q.data.rows.map(enrich);
    // Live-apply the planned draft while editing so Remaining recomputes.
    const eff = rows.map((r) => {
      const code = r.costCode ?? '';
      const d = editing ? planDraft[code] : undefined;
      const planned = d ? (parseNum(d.amount) || 0) : r.planned;
      return { ...r, planned, remaining: r.budget - r.committed - planned };
    });
    const t = eff.reduce((s, r) => ({
      budget: s.budget + r.budget, committed: s.committed + r.committed, spent: s.spent + r.spent,
      planned: s.planned + r.planned, remaining: s.remaining + r.remaining,
    }), { budget: 0, committed: 0, spent: 0, planned: 0, remaining: 0 });
    const overCount = eff.filter((r) => r.committed > r.budget).length;

    // Group for the table.
    let grouped: CmRow[];
    if (view === 'type') {
      const m = new Map<string, CmRow>();
      for (const r of eff) {
        const g = m.get(r.costTypeGroup) ?? { costCode: r.costTypeGroup, costCodeName: null, costTypeGroup: r.costTypeGroup, budgeted: true, budget: 0, committed: 0, spent: 0, planned: 0, plannedVendor: null, plannedNote: null, planStale: false, remaining: 0, over: false, uncommitted: false };
        g.budget += r.budget; g.committed += r.committed; g.spent += r.spent; g.planned += r.planned; g.remaining += r.remaining;
        g.planStale = g.planStale || r.planStale;
        m.set(r.costTypeGroup, g);
      }
      grouped = [...m.values()].map((g) => ({ ...g, over: g.committed > g.budget })).sort((a, b) => b.budget - a.budget);
    } else {
      grouped = eff.slice().sort((a, b) => b.budget - a.budget || (a.costCode ?? '').localeCompare(b.costCode ?? ''));
    }

    const linesByCode = new Map<string, CommitmentLine[]>();
    for (const l of q.data.lines) { const k = l.costCode ?? '—'; (linesByCode.get(k) ?? linesByCode.set(k, []).get(k)!).push(l); }

    const staleCount = eff.filter((r) => r.planStale).length;
    // Suggestions for the Accruals view's cost code and vendor boxes.
    const costCodes = q.data.rows
      .filter((r): r is typeof r & { costCode: string } => !!r.costCode)
      .map((r) => ({ value: r.costCode, label: r.costCodeName ?? '' }))
      .sort((a, b) => a.value.localeCompare(b.value));
    const vendors = [...new Set([
      ...q.data.lines.map((l) => l.vendor),
      ...q.data.rows.map((r) => r.plannedVendor),
    ].filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b));
    return { rows: eff, t, overCount, staleCount, grouped, lines: q.data.lines, linesByCode, prov: q.data.provenance, costCodes, vendors };
  }, [q.data, view, editing, planDraft]);

  if (!showCosts) {
    return <div style={{ padding: 'var(--ew-s-6)', color: 'var(--ew-n-500)', fontSize: 14 }}>Project cost visibility is turned off. An admin can enable it in Admin settings.</div>;
  }

  const startEdit = () => {
    if (!q.data) return;
    setView('code');
    const seed: Record<string, PlanDraft> = {};
    for (const r of q.data.rows) if (r.costCode) seed[r.costCode] = { amount: r.planned ? String(Math.round(r.planned)) : '', vendor: r.plannedVendor ?? '', note: r.plannedNote ?? '' };
    setPlanDraft(seed);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setPlanDraft({}); };
  const savePlan = () => {
    const plans = Object.entries(planDraft).map(([costCode, d]) => ({
      costCode, plannedAmount: parseNum(d.amount) || 0, vendor: d.vendor.trim() || null, note: d.note.trim() || null,
    })).filter((p) => p.plannedAmount > 0 || p.vendor || p.note);
    saveMut.mutate(plans);
  };
  const setDraft = (code: string, patch: Partial<PlanDraft>) =>
    setPlanDraft((d) => {
      const base: PlanDraft = { amount: '', vendor: '', note: '' };
      return { ...d, [code]: { ...base, ...(d[code] ?? {}), ...patch } };
    });
  const toggle = (code: string) => setExpanded((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });

  const filteredLines = (lines: CommitmentLine[]) => {
    const s = search.trim().toLowerCase();
    if (!s) return lines;
    return lines.filter((l) => [l.docNo, l.vendor, l.costCode, l.descr, l.docLabel].some((v) => v?.toLowerCase().includes(s)));
  };

  return (
    <>
      <style>{css}</style>
      <div className="cm-prov">
        <span className="cm-lock">🔒 Finance &amp; PM only</span>
        <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>◇ Synced from Sage Intacct</span>
        {model?.prov.committedSyncedAt && <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>Last synced {fmtDate(model.prov.committedSyncedAt)}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ew-n-500)' }}>POs &amp; subcontracts are entered in Intacct — this view is read-only except the buyout plan.</span>
      </div>

      <StateView query={q} empty="No budget or commitment data for this job yet.">
        {() => {
          if (!model) return null;
          const { t, overCount, staleCount, grouped, lines, linesByCode, costCodes, vendors } = model;
          // The buyout-plan views share a banner, hint and edit button the
          // register and accruals views do not have.
          const planView = view === 'code' || view === 'type';
          const switchLocked = editing || accrualsEditing;
          return (
            <>
              {/* KPI band */}
              <div className="cm-kpis">
                <div className="cm-kpi" style={{ ['--acc' as any]: 'var(--ew-dark)' }}>
                  <div className="l">Budget</div><div className="v">{moneyC(t.budget)}</div>
                </div>
                <div className="cm-kpi" style={{ ['--acc' as any]: 'var(--ew-quality)' }}>
                  <div className="l">Committed</div><div className="v">{moneyC(t.committed)}</div>
                  <div className="s">{t.budget ? Math.round(t.committed / t.budget * 100) : 0}% of budget · PO + sub</div>
                </div>
                <div className="cm-kpi" style={{ ['--acc' as any]: 'var(--ew-n-500)' }}>
                  <div className="l">Spent</div><div className="v">{moneyC(t.spent)}</div>
                  <div className="s">{t.budget ? Math.round(t.spent / t.budget * 100) : 0}% of budget</div>
                </div>
                <div className="cm-kpi" style={{ ['--acc' as any]: 'var(--ew-green)' }}>
                  <div className="l">Remaining to Buy Out</div><div className="v">{moneyC(t.remaining)}</div>
                  <div className="s">budget − committed{t.planned ? ` − planned ${moneyC(t.planned)}` : ''}</div>
                </div>
                <div className="cm-kpi" style={{ ['--acc' as any]: overCount > 0 ? 'var(--ew-performance)' : 'var(--ew-green)' }}>
                  <div className="l">Over-committed</div><div className="v" style={{ color: overCount > 0 ? '#B5611A' : 'var(--ew-green-700)' }}>{overCount} <span style={{ fontSize: 14, color: 'var(--ew-n-500)' }}>codes</span></div>
                  <div className="s">committed above budget</div>
                </div>
              </div>

              <div className="cm-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  <h3>{view === 'register' ? 'Commitment Register' : view === 'accruals' ? 'Month-end Accruals' : 'Budget vs Commitment'}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {view === 'register' && <input className="cm-search" placeholder="Search vendor / PO# / code…" value={search} onChange={(e) => setSearch(e.target.value)} />}
                    <div className="cm-seg">
                      <button className={view === 'code' ? 'active' : ''} disabled={switchLocked} onClick={() => setView('code')}>By Cost Code</button>
                      <button className={view === 'type' ? 'active' : ''} disabled={switchLocked} onClick={() => setView('type')}>By Cost Type</button>
                      <button className={view === 'register' ? 'active' : ''} disabled={switchLocked} onClick={() => setView('register')}>Register</button>
                      <button className={view === 'accruals' ? 'active' : ''} disabled={switchLocked} onClick={() => setView('accruals')}>Accruals</button>
                    </div>
                    {planView && !editing && canEdit && <button className="cm-btn" onClick={startEdit}>✎ Edit Buyout Plan</button>}
                    {editing && <>
                      <button className="cm-btn" onClick={cancelEdit} disabled={saveMut.isPending}>Cancel</button>
                      <button className="cm-btn primary" onClick={savePlan} disabled={saveMut.isPending}>{saveMut.isPending ? 'Saving…' : 'Save Plan'}</button>
                    </>}
                  </div>
                </div>

                {view === 'accruals' ? (
                  <AccrualsPanel projectId={projectId} canEdit={canEdit} costCodes={costCodes} vendors={vendors} onEditingChange={setAccrualsEditing} />
                ) : (<>
                {editing && (
                  <div className="cm-editbanner">✎ Plan the buyout per cost code — enter a <b>planned</b> amount (and optional vendor / note) for work not yet under a PO. <b>Remaining to Buy Out = Budget − Committed − Planned.</b> Expand a row for vendor / note. <b>Planning only — you still create the actual PO/subcontract in Intacct;</b> trim the planned figure as it syncs into Committed.</div>
                )}

                {planView && (
                  <div className="cm-hint"><span>ℹ️</span><span><b>Planned amounts are for planning only</b> — they never post anywhere. Enter the actual PO or subcontract in <b>Sage Intacct</b>; once it syncs into <b>Committed</b> here, trim the matching Planned amount so Remaining to Buy Out stays accurate.</span></div>
                )}
                {planView && staleCount > 0 && (
                  <div className="cm-alert"><span>⚠️</span><span><b>{staleCount} cost code{staleCount === 1 ? '' : 's'} to review</b> — a new commitment has landed in Intacct since the plan was set. Open <b>Edit Buyout Plan</b> and reduce the planned amount{staleCount === 1 ? '' : 's'}.</span></div>
                )}

                {view === 'register' ? (
                  <div className="ew-table-scroll">
                    <table className="cm-tbl">
                      <thead><tr><th>Type</th><th>Doc #</th><th>Vendor</th><th>Cost Code</th><th>Description</th><th>Amount</th></tr></thead>
                      <tbody>
                        {filteredLines(lines).map((l) => (
                          <tr key={l.recordNo}>
                            <td><span className={`cm-typechip ${l.docType === 'Subcontract' ? 'sub' : 'po'}`}>{l.docType === 'Subcontract' ? 'SUB' : 'PO'}</span></td>
                            <td>{l.docNo ?? '—'}</td>
                            <td>{l.vendor ?? '—'}{l.docLabel ? <span style={{ color: 'var(--ew-n-400)' }}> · {l.docLabel}</span> : null}</td>
                            <td>{l.costCode ?? '—'}</td>
                            <td style={{ color: 'var(--ew-n-500)' }}>{l.descr ?? '—'}</td>
                            <td>{money(l.amount)}</td>
                          </tr>
                        ))}
                        {filteredLines(lines).length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--ew-n-500)', padding: 20 }}>No commitments match.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ew-table-scroll">
                    <table className="cm-tbl">
                      <thead>
                        <tr>
                          <th>{view === 'type' ? 'Cost Type' : 'Cost Code'}</th>
                          <th>Budget</th><th>Committed</th><th>Spent</th><th>Planned</th><th>Remaining to Buy Out</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {grouped.map((g) => {
                          const code = g.costCode ?? '—';
                          const isOpen = expanded.has(code);
                          const codeLines = view === 'code' ? (linesByCode.get(code) ?? []) : [];
                          const draft = planDraft[code];
                          return (
                            <Fragment key={code}>
                              <tr className={g.planStale ? 'stale' : g.over ? 'over' : undefined}>
                                <td>
                                  {view === 'code' && <span className="cm-caret" onClick={() => toggle(code)}>{isOpen ? '▾' : '▸'}</span>}
                                  <span style={{ fontWeight: 600 }}>{g.costCode ?? '—'}</span>
                                  {g.costCodeName ? <span style={{ color: 'var(--ew-n-500)', fontWeight: 400 }}> · {g.costCodeName}</span> : null}
                                  {g.planStale && view === 'code' ? <span className="cm-tag stale" title="A PO or subcontract landed in Intacct since you set this plan — reduce the planned amount.">⚠ Reduce plan</span> : null}
                                  {g.over ? <span className="cm-tag over">Over-committed</span> : g.uncommitted && view === 'code' ? <span className="cm-tag open">Not bought out</span> : null}
                                  {!g.budgeted && view === 'code' ? <span className="cm-tag unbud">Unbudgeted</span> : null}
                                </td>
                                <td>{money(g.budget)}</td>
                                <td style={{ color: 'var(--ew-quality)' }}>{g.committed ? money(g.committed) : '—'}</td>
                                <td style={{ color: 'var(--ew-n-500)' }}>{g.spent ? money(g.spent) : '—'}</td>
                                <td>
                                  {editing && view === 'code'
                                    ? <input className="cm-input" inputMode="decimal" value={draft?.amount ?? ''} onChange={(e) => setDraft(code, { amount: e.target.value })} />
                                    : (g.planned ? money(g.planned) : '—')}
                                </td>
                                <td style={{ fontWeight: 600, color: g.remaining < 0 ? '#B5611A' : 'var(--ew-dark)' }}>{g.remaining < 0 ? `(${money(-g.remaining)})` : money(g.remaining)}</td>
                                <td style={{ color: 'var(--ew-n-400)', fontSize: 11 }}>{g.plannedVendor && !editing ? g.plannedVendor : ''}</td>
                              </tr>
                              {view === 'code' && isOpen && (
                                <tr className="cm-lines">
                                  <td colSpan={7} style={{ padding: 0 }}>
                                    {editing && (
                                      <div className="cm-planedit">
                                        <div><label>Planned vendor</label><input style={{ width: 200 }} value={draft?.vendor ?? ''} onChange={(e) => setDraft(code, { vendor: e.target.value })} placeholder="e.g. ABC Concrete" /></div>
                                        <div style={{ flex: 1 }}><label>Note</label><input style={{ width: '100%' }} value={draft?.note ?? ''} onChange={(e) => setDraft(code, { note: e.target.value })} placeholder="buyout note / scope" /></div>
                                      </div>
                                    )}
                                    {!editing && (g.plannedNote || g.plannedVendor) && (
                                      <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--ew-n-600)' }}>
                                        <b>Planned buyout:</b> {g.plannedVendor ?? '—'}{g.plannedNote ? ` · ${g.plannedNote}` : ''}
                                      </div>
                                    )}
                                    {codeLines.length > 0 ? (
                                      <table>
                                        <tbody>
                                          {codeLines.map((l) => (
                                            <tr key={l.recordNo}>
                                              <td><span className={`cm-typechip ${l.docType === 'Subcontract' ? 'sub' : 'po'}`}>{l.docType === 'Subcontract' ? 'SUB' : 'PO'}</span></td>
                                              <td>{l.docNo ?? '—'}</td>
                                              <td>{l.vendor ?? '—'}{l.descr ? <span style={{ color: 'var(--ew-n-400)' }}> · {l.descr}</span> : null}</td>
                                              <td>{money(l.amount)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--ew-n-500)' }}>No committed POs or subcontracts on this code yet.</div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td>{money(t.budget)}</td><td>{money(t.committed)}</td><td>{money(t.spent)}</td>
                          <td>{money(t.planned)}</td>
                          <td style={{ color: t.remaining < 0 ? '#B5611A' : 'var(--ew-dark)' }}>{t.remaining < 0 ? `(${money(-t.remaining)})` : money(t.remaining)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <div className="cm-legend">
                  <span><span style={{ color: 'var(--ew-quality)', fontWeight: 700 }}>Committed</span> = POs + subcontracts issued in Intacct</span>
                  <span><span style={{ color: 'var(--ew-n-500)', fontWeight: 700 }}>Spent ◇</span> = actual cost posted to the GL{!model.prov.actualEnabled ? ' (illustrative until the GL feed is enabled)' : ''}</span>
                  <span><span style={{ color: 'var(--ew-green-700)', fontWeight: 700 }}>Planned</span> = your buyout plan in the Project Tracker, not yet a PO</span>
                </div>
                </>)}
              </div>
            </>
          );
        }}
      </StateView>
    </>
  );
}
